import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const C = { roy: '#85BB65', royDark: '#5E9444', bg: '#F0F2F0', surface: '#FAFBFA', border: '#D8DDD8', text: '#1A1E1A', text2: '#4A5248', text3: '#8A948A', blue: '#3a7abf', green: '#3aaa5e', amber: '#c17f24' };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } };
const stagger = { show: { transition: { staggerChildren: 0.1 } } };

const revenue = [
  { stream: 'Exchange Fees',  desc: '1% on every point exchange between vendors',      color: C.roy },
  { stream: 'Mint Cap Sales', desc: 'Vendors pay monthly for KRT issuance capacity',   color: C.blue },
  { stream: 'Import Haircut', desc: '20% retained on external point imports',          color: C.green },
];

const scale = [
  { users: '50',      monthly: '$1,520',   annual: '$18,240',  note: 'Today' },
  { users: '500',     monthly: '$1,700',   annual: '$20,400',  note: 'Month 6' },
  { users: '5,000',   monthly: '$5,000',   annual: '$60,000',  note: 'Year 1' },
  { users: '50,000',  monthly: '$27,000',  annual: '$324,000', note: 'Year 2' },
  { users: '500,000', monthly: '$127,000', annual: '$1.5M+',   note: 'Scale' },
];

export default function Pitch() {
  return (
    <div style={{ paddingTop: 80, paddingBottom: 96, background: C.bg }}>
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px' }}>

        {/* Header */}
        <motion.div initial="hidden" animate="show" variants={stagger}>
          <motion.p variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 16 }}>
            Investor Brief
          </motion.p>
          <motion.h1 variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 'clamp(48px, 9vw, 88px)', letterSpacing: '-0.03em', color: C.text, lineHeight: 0.95, margin: '0 0 24px' }}>
            The Loyalty<br /><span style={{ color: C.roy }}>Infrastructure<br />Layer</span>
          </motion.h1>
          <motion.p variants={fadeUp} style={{ color: C.text3, fontSize: 17, lineHeight: 1.9, fontFamily: "'Open Sans', sans-serif", maxWidth: 620, margin: 0 }}>
            Korunto is the blockchain protocol connecting every loyalty program into one open ecosystem.
            We are building the infrastructure layer the $50 billion loyalty industry has always been missing.
          </motion.p>
        </motion.div>

        {/* Origin */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          style={{ marginTop: 64, padding: '36px 36px 36px 32px', borderRadius: 14, border: `1px solid ${C.border}`, background: C.surface, borderLeft: `4px solid ${C.roy}` }}>
          <p style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 16 }}>The Origin</p>
          <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 17, color: C.text2, lineHeight: 1.9, fontStyle: 'italic', margin: 0 }}>
            "I was standing at the Boots checkout last Christmas. I had over 3,000 Advantage points on my card.
            I also had Sephora points I had been collecting all year. And I thought — I have all these points
            across all these places and I still cannot afford the one thing I actually want.
            <br /><br />
            Every single loyalty program — a completely separate island. My loyalty was everywhere.
            My value was nowhere. That moment is what built Korunto."
          </p>
        </motion.div>

        {/* Market */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} style={{ marginTop: 80 }}>
          <motion.p variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>Market Opportunity</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 5vw, 52px)', letterSpacing: '-0.02em', color: C.text, lineHeight: 1, margin: '0 0 32px' }}>
            A broken $50B industry
          </motion.h2>
          <motion.div variants={stagger} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { val: '$700B+', label: 'Unredeemed loyalty value worldwide' },
              { val: '3.8B',   label: 'Loyalty program memberships globally' },
              { val: '30%',    label: 'Of points expire unused every year' },
            ].map((s, i) => (
              <motion.div key={i} variants={fadeUp} style={{ padding: '28px', borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface }}>
                <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 48, color: C.roy, lineHeight: 1 }}>{s.val}</div>
                <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3, lineHeight: 1.6, marginTop: 10 }}>{s.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Revenue */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} style={{ marginTop: 80 }}>
          <motion.p variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>Revenue Model</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 5vw, 52px)', letterSpacing: '-0.02em', color: C.text, lineHeight: 1, margin: '0 0 32px' }}>
            Three revenue streams
          </motion.h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {revenue.map((r, i) => (
              <motion.div key={i} variants={fadeUp} style={{ display: 'flex', alignItems: 'flex-start', gap: 20, padding: '22px 24px', borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface }}>
                <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 2, background: r.color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 4 }}>{r.stream}</div>
                  <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text3, lineHeight: 1.6 }}>{r.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Scale */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} style={{ marginTop: 80 }}>
          <motion.p variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>Growth Projections</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 5vw, 52px)', letterSpacing: '-0.02em', color: C.text, lineHeight: 1, margin: '0 0 32px' }}>
            The numbers
          </motion.h2>
          <motion.div variants={fadeUp} style={{ borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden', background: C.surface }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}`, background: C.bg }}>
                  {['Users', 'Monthly', 'Annual', 'Stage'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px 20px', fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.text3, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scale.map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: i === 0 ? 'rgba(133,187,101,0.04)' : 'transparent' }}>
                    <td style={{ padding: '14px 20px', fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 18, color: C.roy }}>{row.users}</td>
                    <td style={{ padding: '14px 20px', fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text2 }}>{row.monthly}</td>
                    <td style={{ padding: '14px 20px', fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 14, color: C.text }}>{row.annual}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: i === 0 ? 'rgba(133,187,101,0.12)' : C.bg, color: i === 0 ? C.royDark : C.text3, border: `1px solid ${i === 0 ? 'rgba(133,187,101,0.25)' : C.border}` }}>{row.note}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </motion.div>

        {/* Ask */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} style={{ marginTop: 80 }}>
          <motion.p variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 11, color: C.roy, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 }}>The Ask</motion.p>
          <motion.h2 variants={fadeUp} style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 'clamp(32px, 5vw, 52px)', letterSpacing: '-0.02em', color: C.text, lineHeight: 1, margin: '0 0 32px' }}>
            Seed round
          </motion.h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <motion.div variants={fadeUp} style={{ padding: '32px', borderRadius: 12, border: `1.5px solid ${C.roy}`, background: 'rgba(133,187,101,0.04)' }}>
              <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 56, color: C.roy, lineHeight: 1 }}>$250K</div>
              <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 14, color: C.text3, marginTop: 6, marginBottom: 24 }}>Seed funding target</div>
              {['Mobile app development', 'Mainnet deployment', 'Vendor acquisition (5 anchors)', 'Legal and compliance'].map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ color: C.roy, fontWeight: 700, fontSize: 14 }}>→</span>
                  <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text2 }}>{u}</span>
                </div>
              ))}
            </motion.div>
            <motion.div variants={fadeUp} style={{ padding: '32px', borderRadius: 12, border: `1px solid ${C.border}`, background: C.surface }}>
              <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 56, color: C.blue, lineHeight: 1 }}>5</div>
              <div style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 14, color: C.text3, marginTop: 6, marginBottom: 24 }}>Anchor vendors needed at launch</div>
              {['Verified real businesses', 'Fixed monthly mint cap fee', 'Co-marketing opportunities', 'Early adopter rate locked'].map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ color: C.blue, fontWeight: 700, fontSize: 14 }}>→</span>
                  <span style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 13, color: C.text2 }}>{u}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          style={{ marginTop: 80, textAlign: 'center', padding: '56px 40px', borderRadius: 16, border: `1px solid ${C.border}`, background: C.surface }}>
          <h3 style={{ fontFamily: "'Raleway', sans-serif", fontWeight: 800, fontSize: 44, color: C.text, letterSpacing: '-0.02em', margin: '0 0 12px' }}>Ready to talk?</h3>
          <p style={{ fontFamily: "'Open Sans', sans-serif", fontSize: 15, color: C.text3, margin: '0 0 36px' }}>
            We are live on testnet today. Contracts deployed, dashboard working, wallet integration complete.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:phoopwintmyatmon@gmail.com" style={{ padding: '13px 28px', borderRadius: 9, background: C.roy, color: '#fff', fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Get in touch →
            </a>
            <Link to="/vendors" style={{ padding: '13px 28px', borderRadius: 9, border: `1.5px solid ${C.border}`, color: C.text2, fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
              Join as Vendor
            </Link>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
